/**
 * Per-endpoint history modals: shows ONLY the build / upload runs for a
 * single branch (the user's "endpoint"), with bulk-select checkboxes and
 * per-row + bulk delete. Replaces the global "Build history" and
 * "Network volume uploads" cards that used to live at the bottom of the
 * Runpod Endpoints view.
 *
 * The list polls every 3 s while there's an active item, every 60 s
 * otherwise. Closing the modal aborts the polling.
 *
 * Each row's "View"/"Watch" button delegates to the SSE detail modal
 * defined in runpod-images.ts via the `openDetail` callback so the per-id
 * live-log experience stays unchanged.
 */
import {
  ApiClient,
  ApiError,
  type RunpodBuildPhase,
  type RunpodBuildRecord,
  type RunpodBuildStatus,
  type RunpodUploadPhase,
  type RunpodUploadRecord,
  type RunpodUploadStatus,
} from '../lib/api.js';
import {
  el,
  formatBytes,
  formatDateTime,
  formatDuration,
  mount,
  statusPill,
  type PillKind,
} from '../lib/dom.js';

const buildPhaseLabel: Record<RunpodBuildPhase, string> = {
  queued: 'queued',
  cloning: 'cloning git repo',
  building: 'docker build',
  pushing: 'pushing to Docker Hub',
  cleanup: 'cleaning local image',
  done: 'done',
};

const uploadPhaseLabel: Record<RunpodUploadPhase, string> = {
  queued: 'queued',
  preparing: 'preparing script',
  creating_pod: 'creating pod',
  waiting: 'waiting for pod',
  downloading: 'downloading',
  verifying: 'verifying',
  cleanup: 'tearing down pod',
  done: 'done',
};

const statusToPill = (s: RunpodBuildStatus | RunpodUploadStatus): PillKind => {
  if (s === 'canceled') return 'warning';
  return s as PillKind;
};

const buildPill = (b: RunpodBuildRecord): { kind: PillKind; label: string } => {
  if (b.status !== 'running') return { kind: statusToPill(b.status), label: b.status };
  switch (b.phase) {
    case 'pushing':
    case 'cleanup':
      return { kind: 'running', label: 'uploading to runpod' };
    default:
      return { kind: 'running', label: 'building' };
  }
};

interface ModalContext {
  selected: Set<number>;
  liveTickers: ReturnType<typeof setInterval>[];
  nextTimer: ReturnType<typeof setTimeout> | null;
  closed: boolean;
}

/**
 * Mounts a backdrop + modal frame and returns the inner body element where
 * the caller can append its own toolbar / list. Handles ESC + click-out
 * close, returns a finalize() that the polling loop should chain its
 * cleanup into.
 */
function buildModalShell(title: string): {
  body: HTMLElement;
  toolbar: HTMLElement;
  alertBox: HTMLElement;
  close: () => void;
  onClose: (cb: () => void) => void;
} {
  const backdrop = el('div', { class: 'modal-backdrop' });
  const closeBtn = el('button', { class: 'ghost', text: 'Close' });
  const toolbar = el('div', { class: 'toolbar' });
  const alertBox = el('div', { class: 'alert', attrs: { hidden: true, role: 'alert' } });
  const body = el('div', { class: 'logs-list' });

  const modal = el('div', {
    class: 'modal modal-wide',
    children: [
      el('header', {
        children: [
          el('div', {
            class: 'modal-title',
            children: [el('h3', { text: title })],
          }),
          el('div', { class: 'log-actions', children: [closeBtn] }),
        ],
      }),
      alertBox,
      toolbar,
      body,
    ],
  });
  backdrop.append(modal);
  document.body.append(backdrop);

  const closeCallbacks: (() => void)[] = [];
  const close = () => {
    for (const cb of closeCallbacks) {
      try { cb(); } catch { /* ignore */ }
    }
    backdrop.remove();
    document.removeEventListener('keydown', escHandler);
  };
  closeBtn.addEventListener('click', close);
  backdrop.addEventListener('click', (ev) => {
    if (ev.target === backdrop) close();
  });
  function escHandler(ev: KeyboardEvent) {
    if (ev.key === 'Escape') close();
  }
  document.addEventListener('keydown', escHandler);

  return { body, toolbar, alertBox, close, onClose: (cb) => closeCallbacks.push(cb) };
}

function showAlert(box: HTMLElement, msg: string, kind: 'error' | 'success' = 'error'): void {
  box.className = kind === 'success' ? 'alert success' : 'alert';
  box.textContent = msg;
  box.removeAttribute('hidden');
  setTimeout(() => box.setAttribute('hidden', ''), 6000);
}

/* ─── Build history modal ───────────────────────────────────── */

export function openBuildHistoryModal(deps: {
  api: ApiClient;
  branch: string;
  /** Existing per-id SSE-watching modal — provided so this file doesn't
   *  duplicate the live log viewer. */
  openDetail: (id: number) => void;
}): void {
  const { api, branch, openDetail } = deps;
  const ctx: ModalContext = {
    selected: new Set(),
    liveTickers: [],
    nextTimer: null,
    closed: false,
  };
  const shell = buildModalShell(`Builds · ${branch}`);
  shell.onClose(() => {
    ctx.closed = true;
    if (ctx.nextTimer) clearTimeout(ctx.nextTimer);
    for (const t of ctx.liveTickers) clearInterval(t);
  });

  const summary = el('span', { class: 'who' });
  const refreshBtn = el('button', { class: 'ghost', text: 'Refresh' });
  const deleteBtn = el('button', {
    class: 'danger',
    text: 'Delete selected',
    attrs: { disabled: true },
  });
  const selectAllCheckbox = el('input', {
    attrs: { type: 'checkbox', 'aria-label': 'Select all' },
  });
  shell.toolbar.append(
    el('label', {
      class: 'history-select-all',
      children: [selectAllCheckbox, el('span', { text: 'Select all' })],
    }),
    deleteBtn,
    el('span', { class: 'spacer' }),
    summary,
    refreshBtn,
  );

  let current: RunpodBuildRecord[] = [];

  const updateDeleteBtn = () => {
    deleteBtn.toggleAttribute('disabled', ctx.selected.size === 0);
    deleteBtn.textContent = ctx.selected.size > 0
      ? `Delete selected (${ctx.selected.size})`
      : 'Delete selected';
  };

  const renderRow = (b: RunpodBuildRecord): HTMLElement => {
    const pill = buildPill(b);
    const checkbox = el('input', {
      attrs: {
        type: 'checkbox',
        'aria-label': `Select build #${b.id}`,
        // Running builds can't be deleted — disable the checkbox to
        // signal that, otherwise the user could pick something the
        // server will reject.
        disabled: b.status === 'running',
      },
    });
    if (ctx.selected.has(b.id)) checkbox.checked = true;
    checkbox.addEventListener('change', () => {
      if (checkbox.checked) ctx.selected.add(b.id);
      else ctx.selected.delete(b.id);
      updateDeleteBtn();
      // Sync the master checkbox.
      const all = current.filter((x) => x.status !== 'running');
      selectAllCheckbox.checked = all.length > 0 && all.every((x) => ctx.selected.has(x.id));
    });

    const head = el('div', {
      class: 'log-row-head',
      children: [
        statusPill(pill.kind, pill.label),
        el('span', {
          class: 'who',
          text: `#${b.id} → ${b.dockerhubRepo}:${b.dockerhubTag}`,
        }),
        b.phase && b.status === 'running'
          ? el('span', { class: 'who', text: `· ${buildPhaseLabel[b.phase]}` })
          : null,
      ].filter((x): x is HTMLElement => x !== null),
    });
    const when = el('div', {
      class: 'when',
      text: `${formatDateTime(b.startedAt)} · ${formatDuration(b.startedAt, b.finishedAt)}`,
    });
    if (b.status === 'running') {
      const id = setInterval(() => {
        if (!when.isConnected) {
          clearInterval(id);
          return;
        }
        when.textContent = `${formatDateTime(b.startedAt)} · ${formatDuration(b.startedAt, null)}`;
      }, 1000);
      ctx.liveTickers.push(id);
    }
    const who = el('div', {
      class: 'who',
      text: [
        `by ${b.username}`,
        b.commitSha ? b.commitSha.slice(0, 7) : null,
        b.imageSize != null ? `image: ${formatBytes(b.imageSize)}` : null,
        b.errorMessage,
      ].filter((x): x is string => Boolean(x)).join(' · '),
    });
    const meta = el('div', { class: 'log-meta', children: [head, when, who] });

    const viewBtn = el('button', {
      text: b.status === 'running' ? 'Watch' : 'View',
      on: {
        click: (ev) => {
          ev.stopPropagation();
          openDetail(b.id);
        },
      },
    });

    const actions: HTMLElement[] = [viewBtn];
    if (b.status === 'success' && b.immutableTag) {
      const fullRef = `${b.dockerhubRepo}:${b.immutableTag}`;
      const copyBtn = el('button', {
        text: 'Copy ref',
        attrs: { title: `Copy ${fullRef} to clipboard` },
        on: {
          click: async (ev) => {
            ev.stopPropagation();
            try {
              await navigator.clipboard.writeText(fullRef);
              const orig = copyBtn.textContent;
              copyBtn.textContent = '✓ Copied';
              setTimeout(() => { copyBtn.textContent = orig; }, 1500);
            } catch {
              showAlert(shell.alertBox, 'Could not access clipboard');
            }
          },
        },
      });
      actions.push(copyBtn);
    }
    if (b.status !== 'running') {
      const delBtn = el('button', {
        class: 'danger',
        text: 'Delete',
        on: {
          click: async (ev) => {
            ev.stopPropagation();
            if (!confirm(`Delete build #${b.id} and its log?`)) return;
            try {
              await api.deleteRunpodBuild(b.id);
              ctx.selected.delete(b.id);
              showAlert(shell.alertBox, `Build #${b.id} deleted`, 'success');
              await refresh();
            } catch (err) {
              showAlert(shell.alertBox, err instanceof ApiError ? err.message : 'Delete failed');
            }
          },
        },
      });
      actions.push(delBtn);
    }

    return el('div', {
      class: 'log-row',
      on: { click: () => openDetail(b.id) },
      children: [
        el('div', { class: 'log-row-checkbox', children: [checkbox] }),
        meta,
        el('div', { class: 'log-actions', children: actions }),
      ],
    });
  };

  const scheduleNext = (delay: number) => {
    if (ctx.nextTimer) clearTimeout(ctx.nextTimer);
    ctx.nextTimer = setTimeout(() => {
      if (!ctx.closed) void refresh();
    }, delay);
  };

  const refresh = async () => {
    if (ctx.closed) return;
    refreshBtn.setAttribute('disabled', '');
    try {
      const { items, active } = await api.listRunpodBuilds({ limit: 100, branch });
      current = items;
      summary.textContent = items.length
        ? `${items.length} for this endpoint · ${active.length} active`
        : '';
      // Drop selections for items that no longer exist or just turned
      // running (server would reject the delete anyway).
      const validIds = new Set(items.filter((b) => b.status !== 'running').map((b) => b.id));
      for (const id of [...ctx.selected]) if (!validIds.has(id)) ctx.selected.delete(id);
      for (const t of ctx.liveTickers) clearInterval(t);
      ctx.liveTickers.length = 0;
      if (items.length === 0) {
        mount(shell.body, [el('div', { class: 'empty', text: 'No builds for this endpoint yet.' })]);
      } else {
        mount(shell.body, items.map(renderRow));
      }
      updateDeleteBtn();
      const all = items.filter((b) => b.status !== 'running');
      selectAllCheckbox.checked = all.length > 0 && all.every((b) => ctx.selected.has(b.id));
      scheduleNext(active.length > 0 ? 3_000 : 60_000);
    } catch (err) {
      showAlert(shell.alertBox, err instanceof ApiError ? err.message : 'Failed to load builds');
      scheduleNext(15_000);
    } finally {
      refreshBtn.removeAttribute('disabled');
    }
  };

  selectAllCheckbox.addEventListener('change', () => {
    if (selectAllCheckbox.checked) {
      for (const b of current) {
        if (b.status !== 'running') ctx.selected.add(b.id);
      }
    } else {
      ctx.selected.clear();
    }
    void refresh();
  });

  deleteBtn.addEventListener('click', async () => {
    const ids = [...ctx.selected];
    if (ids.length === 0) return;
    if (!confirm(`Delete ${ids.length} build${ids.length > 1 ? 's' : ''}?`)) return;
    deleteBtn.setAttribute('disabled', '');
    try {
      const { results } = await api.deleteRunpodBuilds(ids);
      const failures = results.filter((r) => !r.ok);
      ctx.selected.clear();
      if (failures.length > 0) {
        showAlert(shell.alertBox,
          `Deleted ${results.length - failures.length}/${results.length}; ` +
          `skipped: ${failures.map((f) => `#${f.id} (${f.reason})`).join(', ')}`,
        );
      } else {
        showAlert(shell.alertBox, `Deleted ${results.length} build(s)`, 'success');
      }
      await refresh();
    } catch (err) {
      showAlert(shell.alertBox, err instanceof ApiError ? err.message : 'Bulk delete failed');
    }
  });

  refreshBtn.addEventListener('click', () => void refresh());
  void refresh();
}

/* ─── Upload history modal ──────────────────────────────────── */

export function openUploadHistoryModal(deps: {
  api: ApiClient;
  branch: string;
  openDetail: (id: number) => void;
}): void {
  const { api, branch, openDetail } = deps;
  const ctx: ModalContext = {
    selected: new Set(),
    liveTickers: [],
    nextTimer: null,
    closed: false,
  };
  const shell = buildModalShell(`Volume uploads · ${branch}`);
  shell.onClose(() => {
    ctx.closed = true;
    if (ctx.nextTimer) clearTimeout(ctx.nextTimer);
    for (const t of ctx.liveTickers) clearInterval(t);
  });

  const summary = el('span', { class: 'who' });
  const refreshBtn = el('button', { class: 'ghost', text: 'Refresh' });
  const deleteBtn = el('button', {
    class: 'danger',
    text: 'Delete selected',
    attrs: { disabled: true },
  });
  const selectAllCheckbox = el('input', {
    attrs: { type: 'checkbox', 'aria-label': 'Select all' },
  });
  shell.toolbar.append(
    el('label', {
      class: 'history-select-all',
      children: [selectAllCheckbox, el('span', { text: 'Select all' })],
    }),
    deleteBtn,
    el('span', { class: 'spacer' }),
    summary,
    refreshBtn,
  );

  let current: RunpodUploadRecord[] = [];

  const updateDeleteBtn = () => {
    deleteBtn.toggleAttribute('disabled', ctx.selected.size === 0);
    deleteBtn.textContent = ctx.selected.size > 0
      ? `Delete selected (${ctx.selected.size})`
      : 'Delete selected';
  };

  const renderRow = (u: RunpodUploadRecord): HTMLElement => {
    const phase = u.phase ? uploadPhaseLabel[u.phase] : '';
    const pill = statusPill(statusToPill(u.status), u.status);
    const checkbox = el('input', {
      attrs: {
        type: 'checkbox',
        'aria-label': `Select upload #${u.id}`,
        disabled: u.status === 'running',
      },
    });
    if (ctx.selected.has(u.id)) checkbox.checked = true;
    checkbox.addEventListener('change', () => {
      if (checkbox.checked) ctx.selected.add(u.id);
      else ctx.selected.delete(u.id);
      updateDeleteBtn();
      const all = current.filter((x) => x.status !== 'running');
      selectAllCheckbox.checked = all.length > 0 && all.every((x) => ctx.selected.has(x.id));
    });

    const head = el('div', {
      class: 'log-row-head',
      children: [
        pill,
        el('span', { class: 'who', text: `#${u.id} → ${u.networkVolumeId} (${u.dataCenterId})` }),
        u.status === 'running' && phase
          ? el('span', { class: 'who', text: `· ${phase}` })
          : null,
      ].filter((x): x is HTMLElement => x !== null),
    });
    const when = el('div', {
      class: 'when',
      text: `${formatDateTime(u.startedAt)} · ${formatDuration(u.startedAt, u.finishedAt)}`,
    });
    if (u.status === 'running') {
      const id = setInterval(() => {
        if (!when.isConnected) {
          clearInterval(id);
          return;
        }
        when.textContent = `${formatDateTime(u.startedAt)} · ${formatDuration(u.startedAt, null)}`;
      }, 1000);
      ctx.liveTickers.push(id);
    }
    const failed = u.modelsFailed > 0 ? ` · ${u.modelsFailed} failed` : '';
    const who = el('div', {
      class: 'who',
      text: [
        `by ${u.username}`,
        `${u.modelsDone}/${u.modelsTotal} models${failed}`,
        u.bytesDownloaded > 0 ? formatBytes(u.bytesDownloaded) : null,
        u.errorMessage,
      ].filter((x): x is string => Boolean(x)).join(' · '),
    });
    const meta = el('div', { class: 'log-meta', children: [head, when, who] });

    const viewBtn = el('button', {
      text: u.status === 'running' ? 'Watch' : 'View',
      on: {
        click: (ev) => {
          ev.stopPropagation();
          openDetail(u.id);
        },
      },
    });

    const actions: HTMLElement[] = [viewBtn];
    if (u.status !== 'running') {
      const delBtn = el('button', {
        class: 'danger',
        text: 'Delete',
        on: {
          click: async (ev) => {
            ev.stopPropagation();
            if (!confirm(`Delete upload #${u.id} and its log?`)) return;
            try {
              await api.deleteRunpodVolumeUpload(u.id);
              ctx.selected.delete(u.id);
              showAlert(shell.alertBox, `Upload #${u.id} deleted`, 'success');
              await refresh();
            } catch (err) {
              showAlert(shell.alertBox, err instanceof ApiError ? err.message : 'Delete failed');
            }
          },
        },
      });
      actions.push(delBtn);
    }

    return el('div', {
      class: 'log-row',
      on: { click: () => openDetail(u.id) },
      children: [
        el('div', { class: 'log-row-checkbox', children: [checkbox] }),
        meta,
        el('div', { class: 'log-actions', children: actions }),
      ],
    });
  };

  const scheduleNext = (delay: number) => {
    if (ctx.nextTimer) clearTimeout(ctx.nextTimer);
    ctx.nextTimer = setTimeout(() => {
      if (!ctx.closed) void refresh();
    }, delay);
  };

  const refresh = async () => {
    if (ctx.closed) return;
    refreshBtn.setAttribute('disabled', '');
    try {
      const { items, active } = await api.listRunpodVolumeUploads({ limit: 100, branch });
      current = items;
      summary.textContent = items.length
        ? `${items.length} for this endpoint · ${active.length} active`
        : '';
      const validIds = new Set(items.filter((u) => u.status !== 'running').map((u) => u.id));
      for (const id of [...ctx.selected]) if (!validIds.has(id)) ctx.selected.delete(id);
      for (const t of ctx.liveTickers) clearInterval(t);
      ctx.liveTickers.length = 0;
      if (items.length === 0) {
        mount(shell.body, [el('div', { class: 'empty', text: 'No volume uploads for this endpoint yet.' })]);
      } else {
        mount(shell.body, items.map(renderRow));
      }
      updateDeleteBtn();
      const all = items.filter((u) => u.status !== 'running');
      selectAllCheckbox.checked = all.length > 0 && all.every((u) => ctx.selected.has(u.id));
      scheduleNext(active.length > 0 ? 5_000 : 60_000);
    } catch (err) {
      showAlert(shell.alertBox, err instanceof ApiError ? err.message : 'Failed to load uploads');
      scheduleNext(15_000);
    } finally {
      refreshBtn.removeAttribute('disabled');
    }
  };

  selectAllCheckbox.addEventListener('change', () => {
    if (selectAllCheckbox.checked) {
      for (const u of current) {
        if (u.status !== 'running') ctx.selected.add(u.id);
      }
    } else {
      ctx.selected.clear();
    }
    void refresh();
  });

  deleteBtn.addEventListener('click', async () => {
    const ids = [...ctx.selected];
    if (ids.length === 0) return;
    if (!confirm(`Delete ${ids.length} upload${ids.length > 1 ? 's' : ''}?`)) return;
    deleteBtn.setAttribute('disabled', '');
    try {
      const { results } = await api.deleteRunpodVolumeUploads(ids);
      const failures = results.filter((r) => !r.ok);
      ctx.selected.clear();
      if (failures.length > 0) {
        showAlert(shell.alertBox,
          `Deleted ${results.length - failures.length}/${results.length}; ` +
          `skipped: ${failures.map((f) => `#${f.id} (${f.reason})`).join(', ')}`,
        );
      } else {
        showAlert(shell.alertBox, `Deleted ${results.length} upload(s)`, 'success');
      }
      await refresh();
    } catch (err) {
      showAlert(shell.alertBox, err instanceof ApiError ? err.message : 'Bulk delete failed');
    }
  });

  refreshBtn.addEventListener('click', () => void refresh());
  void refresh();
}
